import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IVerticalTabProps } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { useDepartment, usePermissions } from "@mibuildweb/hooks";
import { Typography } from "@mui/material";
import { DepartmentConfigDetailsTabView } from "./tabs/DepartmentConfigDetailsTabView";
import { DepartmentConfigModulesTabView } from "./tabs/DepartmentConfigModulesTabView";
import { DepartmentConfigUsersListTabView } from "./tabs/DepartmentConfigUsersListTabView";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { DepartmentConfigData } from "../ConfigDepartments.types";
import { configDepartmentSchema } from "../schemas/ConfigDepartment.schema";
import { useDepartmentDetailsViewModel } from "./useDepartmentDetailsViewModel";

const INITIAL_DEPARTMENT_DATA: DepartmentConfigData = {
  name: "",
  description: "",
  abbreviation: "",
  modules: [],
  users: [],
};

export const ConfigDepartmentDetails = () => {
  const { params, goBack } = useRouter();
  const { id: departmentId } = params;

  const { department, getDepartment, updateDepartment, loading } =
    useDepartment();

  const { hasSomePermissions } = usePermissions();

  const canCreateDepartment = hasSomePermissions([
    PERMISSIONS.DEPARTMENTS.CREATE,
    PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
  ]);
  const canUpdateDepartment = hasSomePermissions([
    PERMISSIONS.DEPARTMENTS.UPDATE,
    PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION,
  ]);

  const { handleCreateDepartment } = useDepartmentDetailsViewModel();

  const form = useForm<DepartmentConfigData>({
    mode: "onChange",
    defaultValues: INITIAL_DEPARTMENT_DATA,
    resolver: zodResolver(configDepartmentSchema),
  });

  const {
    control,
    formState: { isValid, isDirty },
    getValues,
    reset,
  } = form;

  const watchName = useWatch({
    control,
    name: "name",
  });

  const areModulesComplete = useWatch({
    control,
    compute: (values) => {
      return values.modules.length > 0;
    },
  });

  const usersCount = useWatch({
    control,
    compute: (values) => {
      return values.users?.length || 0;
    },
  });

  const tabs = useMemo<IVerticalTabProps<DepartmentConfigData>[]>(() => {
    return [
      {
        label: "Department Details",
        value: "details",
        controlledFormViewComponent: DepartmentConfigDetailsTabView,
        isComplete: isValid,
        hasErrors: !isValid,
      },
      {
        label: "Modules",
        value: "modules",
        controlledFormViewComponent: DepartmentConfigModulesTabView,
        isComplete: areModulesComplete,
      },
      {
        label: "Users List",
        value: "usersList",
        isComplete: usersCount > 0,
        extraTabProps: {
          departmentId,
        },
        controlledFormViewComponent: DepartmentConfigUsersListTabView,
      },
    ];
  }, [isValid, areModulesComplete, usersCount, departmentId]);

  const handleFormReset = () => {
    if (department) {
      reset({
        id: department.id,
        name: department.name,
        description: department.description,
        abbreviation: department.abbreviation,
        modules: department.modules,
        users: department.users,
      });
    } else {
      reset(INITIAL_DEPARTMENT_DATA);
    }
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        reset();
        goBack();
      },
    },
    submit: {
      label: "Save Department",
      onClick: () => {
        const values = getValues();
        if (department) {
          updateDepartment(department.id, values);
        } else {
          handleCreateDepartment(values);
        }
      },
      disabled:
        !isDirty ||
        !isValid ||
        loading ||
        !canCreateDepartment ||
        !canUpdateDepartment,
    },
  };

  const renderPageTitle = () => {
    const action = departmentId ? "Edit" : "Create";
    const departmentLabel = watchName ? `${watchName}` : "New Department";
    return (
      <Typography variant="HD4">
        {action} Department:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {departmentLabel}
        </Typography>
      </Typography>
    );
  };

  useEffect(() => {
    if (departmentId) {
      getDepartment(departmentId);
    }
  }, [departmentId]);

  useEffect(() => {
    handleFormReset();
  }, [department]);

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={actions}
      loading={loading}
    >
      <VerticalTabsLayout form={form} tabs={tabs} transparentBackground />
    </ConfigPageLayout>
  );
};
